import React, { useLayoutEffect } from 'react';
import { type HoverPopoverAnchorReference } from '@components/ui/HoverPopover/HoverPopover.types';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  type Placement,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { Box } from '@mui/material';

import { useFilterMenuTokens } from './filterMenuTokens';

/**
 * Presses inside MUI-portaled layers (the DatePicker calendar popper, Select
 * menus…) belong to content rendered *by* the cascade and must never count as
 * an outside press against it.
 */
// .MuiPickerPopper-root is the MUI X v9 name; the plural .MuiPickersPopper-root
// is kept for older class names surviving version drift.
const isInsideMuiPortal = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  Boolean(
    target.closest(
      '.MuiPickerPopper-root, .MuiPickersPopper-root, .MuiPopover-root, .MuiModal-root, .MuiAutocomplete-popper',
    ),
  );

interface FilterPanelSurfaceProps {
  open: boolean;
  anchorReference: HoverPopoverAnchorReference | null;
  /** floating-ui asked to dismiss (escape / outside press). */
  onClose: () => void;
  placement: Placement;
  fallbackPlacements: Placement[];
  /** Disabled on the root panel while its sub-panel is open — Esc closes innermost first. */
  escapeKeyEnabled?: boolean;
  /** Return true for presses that belong to a sibling panel (they must not dismiss this one). */
  isPressInside?: (target: EventTarget | null) => boolean;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  /** Reports the floating element so the sibling panel can hit-test against it. */
  onElementChange?: (element: HTMLElement | null) => void;
  width: number;
  zIndex: number;
  ariaLabel: string;
  children: React.ReactNode;
}

/**
 * The positioned, portaled container for one cascade panel. Owns floating-ui
 * placement + dismissal only; open/close *intent* (hover timers, which field
 * is active) lives in the caller. Adapted from HoverPopoverSurface minus its
 * hover-card animation/pin semantics, which are wrong for a menu.
 */
export const FilterPanelSurface: React.FC<FilterPanelSurfaceProps> = ({
  open,
  anchorReference,
  onClose,
  placement,
  fallbackPlacements,
  escapeKeyEnabled = true,
  isPressInside,
  onPointerEnter,
  onPointerLeave,
  onElementChange,
  width,
  zIndex,
  ariaLabel,
  children,
}) => {
  const tokens = useFilterMenuTokens();

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: nextOpen => {
      if (!nextOpen) onClose();
    },
    placement,
    transform: false,
    middleware: [offset(6), flip({ fallbackPlacements }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context, {
    escapeKey: escapeKeyEnabled,
    outsidePress: event => {
      if (isInsideMuiPortal(event.target)) return false;
      if (isPressInside?.(event.target)) return false;
      return true;
    },
  });
  const role = useRole(context, { role: 'dialog' });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  useLayoutEffect(() => {
    if (open && anchorReference) refs.setPositionReference(anchorReference);
  }, [open, anchorReference, refs]);

  if (!open || !anchorReference) return null;

  return (
    <FloatingPortal>
      <Box
        ref={(element: HTMLElement | null) => {
          refs.setFloating(element);
          onElementChange?.(element);
        }}
        style={{ ...floatingStyles, zIndex }}
        {...getFloatingProps({
          'aria-label': ariaLabel,
          onClick: event => event.stopPropagation(),
          onMouseEnter: () => onPointerEnter?.(),
          onMouseLeave: () => onPointerLeave?.(),
        })}
        sx={{
          bgcolor: tokens.surfaceBg,
          border: `1px solid ${tokens.line}`,
          borderRadius: `${tokens.radius}px`,
          boxShadow: tokens.shadow,
          overflow: 'hidden',
          width,
        }}
      >
        {children}
      </Box>
    </FloatingPortal>
  );
};
